'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

/**
 * Shigo buttons use compact control radii, semantic primary tokens, and ship
 * Inter 500 at 13px by default. On touch screens an invisible ::before grows the hit area
 * to at least 44x44 without changing the button's size or the layout around it.
 */
const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 pointer-coarse:before:absolute pointer-coarse:before:inset-[min(0px,calc((100%-44px)/2))] pointer-coarse:before:content-[""] whitespace-nowrap rounded-md font-medium cursor-pointer transition-[color,background-color,border-color,opacity,transform] duration-[var(--duration-fluid)] ease-fluid active:scale-[0.97] active:duration-[var(--duration-press)] active:ease-out motion-reduce:transform-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:opacity-90',
        teal: 'bg-[var(--teal-primary)] text-white hover:opacity-90',
        outline:
          'border border-[var(--border-strong)] bg-[var(--bg-white)] text-[var(--text-primary)] hover:bg-[var(--bg-page)] disabled:bg-[var(--secondary)] disabled:text-[var(--text-disabled)]',
        ghost: 'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-page)]',
        destructive: 'bg-[var(--negative)] text-white hover:opacity-90',
        link: 'text-accent-foreground underline-offset-4 hover:underline rounded-none px-0 active:scale-100',
        secondary:
          'border border-[var(--border-default)] bg-[var(--bg-page)] text-[var(--text-primary)] hover:bg-[var(--bg-white)] disabled:bg-[var(--secondary)] disabled:text-[var(--text-disabled)]',
      },
      size: {
        default: 'h-9 px-4 text-[13px]',
        sm: 'h-7 px-3 text-[12px]',
        lg: 'h-10 px-6 text-[14px]',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    );
  },
);
Button.displayName = 'Button';

// Allowlisted in config-eslint's react-refresh `allowExportNames` (shadcn layout).
// react-doctor-disable-next-line react-doctor/only-export-components
export { Button, buttonVariants };
